import { component } from 'picoapp';

export default component(async (node) => {
  const { body } = document;

  const lightboxElem = document.querySelector('[data-stl-lightbox]');
  const sliderElem = lightboxElem?.querySelector('[data-stl-lightbox-slider]');
  const prevButton = lightboxElem?.querySelector('[data-stl-lightbox-prev]');
  const nextButton = lightboxElem?.querySelector('[data-stl-lightbox-next]');
  const scrollContainers = lightboxElem?.querySelectorAll('[data-stl-lightbox-scroll-container]');
  const activeIndexElem = lightboxElem?.querySelector('[data-stl-lightbox-active-index]');
  const totalCountElem = lightboxElem?.querySelector('[data-stl-lightbox-total]');
  const navigationElem = lightboxElem?.querySelector('[data-stl-lightbox-navigation]');
  const counterElem = lightboxElem?.querySelector('[data-stl-lightbox-counter]');

  let slider;
  let isOpen = false;

  const swiperConfig = {
    slidesPerView: 1,
    init: false,
    navigation: {
      prevEl: prevButton,
      nextEl: nextButton,
    },
    noSwiping: true,
    breakpoints: {
      1100: {
        noSwiping: false,
      },
    },
  };

  /**
   * Initialize Swiper for lightbox
   */
  const initSwiper = async () => {
    if (!sliderElem) return;

    const Swiper = (await import(
      /* webpackChunkName: "swiper" */
      'modules/swiper'
    )).default;

    slider = new Swiper(sliderElem, swiperConfig);
    slider.init();

    // Update active index when sliding
    slider.on('slideChange', (info) => {
      const { activeIndex } = info;
      if (activeIndexElem) {
        activeIndexElem.textContent = activeIndex + 1;
      }
    });
  };

  /**
   * Center images in lightbox
   */
  const centerImages = () => {
    if (!scrollContainers) return;

    scrollContainers.forEach((container) => {
      const img = container.querySelector('img');
      if (img) {
        const imageWidth = (img.getBoundingClientRect().width / 2) - (window.innerWidth / 2);
        container.scrollLeft = imageWidth;
      }
    });
  };

  /**
   * Close lightbox
   */
  const closeLightbox = () => {
    if (!lightboxElem) return;

    body.classList.remove('no-scroll');
    lightboxElem.hidden = true;
    lightboxElem.classList.add('pointer-events-none', 'opacity-0');
    isOpen = false;
  };

  /**
   * Update lightbox images with new product data
   */
  const updateLightboxImages = (productData) => {
    if (!lightboxElem || !sliderElem || !productData.images) return;

    const swiperWrapper = sliderElem.querySelector('.swiper-wrapper');

    if (!swiperWrapper) return;

    // Generate new slides HTML
    // Update the wrapper
    swiperWrapper.innerHTML = productData.images.map((imageData) => {
      // Handle different image data formats
      let imageUrl;
      if (typeof imageData === 'string') {
        imageUrl = imageData;
      } else if (imageData && imageData.src) {
        imageUrl = imageData.src;
      } else if (imageData && imageData.url) {
        imageUrl = imageData.url;
      } else {
        return '';
      }

      // Add https: protocol if missing
      const fullImageUrl = imageUrl.startsWith('//') ? `https:${imageUrl}` : imageUrl;

      return `
        <div class="swiper-slide swiper-no-swiping">
          <div class="lightbox__scroll-container" data-stl-lightbox-scroll-container data-stl-lightbox-trigger>
            <img class="max-w-[unset] h-full mx-auto cursor-zoom-out" src="${fullImageUrl}" alt="${productData.title}">
          </div>
        </div>
      `;
    }).filter((slide) => slide !== '').join('');

    // Update total count
    if (totalCountElem) {
      totalCountElem.textContent = productData.images.length;
    }

    // Update navigation visibility
    if (navigationElem && counterElem) {
      if (productData.images.length <= 1) {
        navigationElem.style.display = 'none';
        counterElem.style.display = 'none';
      } else {
        navigationElem.style.display = '';
        counterElem.style.display = '';
      }
    }

    // Update Swiper if it exists
    if (slider) {
      slider.update();

      // Re-attach click listeners to new scroll containers
      const newScrollContainers = lightboxElem.querySelectorAll('[data-stl-lightbox-scroll-container]');
      newScrollContainers.forEach((container) => {
        container.addEventListener('click', closeLightbox);
      });
    }
  };

  /**
   * Open lightbox at specific index
   */
  const openLightbox = (index = 0) => {
    if (!lightboxElem || !slider) return;

    setTimeout(() => {
      body.classList.add('no-scroll');
    }, 300);

    slider.slideTo(index);

    if (activeIndexElem) {
      activeIndexElem.textContent = index + 1;
    }

    lightboxElem.hidden = false;

    // Center all images
    setTimeout(() => {
      centerImages();
    }, 100);

    setTimeout(() => {
      lightboxElem.classList.remove('pointer-events-none', 'opacity-0');
    }, 200);

    isOpen = true;
  };

  /**
   * Handle zoom button clicks
   */
  const handleZoomButtonClick = (event) => {
    const zoomButton = event.target.closest('[data-lightbox-open]');
    if (!zoomButton) return;

    event.preventDefault();
    const slideIndex = parseInt(zoomButton.dataset.index || '0', 10);
    openLightbox(slideIndex);
  };

  /**
   * Handle STL lightbox update events
   */
  const handleLightboxUpdate = (event) => {
    const { productData } = event.detail;
    if (productData) {
      updateLightboxImages(productData);
    }
  };

  /**
   * Initialize component
   */
  const init = async () => {
    if (!lightboxElem) {
      console.warn('STL Lightbox: No lightbox element found');
      return;
    }

    await initSwiper();

    // Add click listeners to close triggers
    const closeTriggers = lightboxElem.querySelectorAll('[data-stl-lightbox-trigger]');
    closeTriggers.forEach((trigger) => {
      trigger.addEventListener('click', closeLightbox);
    });

    // Listen for zoom button clicks in the STL area
    node.addEventListener('click', handleZoomButtonClick);

    // Also add a fallback listener to document
    document.addEventListener('click', (event) => {
      const zoomButton = event.target.closest('[data-lightbox-open]');
      if (zoomButton) {
        event.preventDefault();
        const slideIndex = parseInt(zoomButton.dataset.index || '0', 10);
        openLightbox(slideIndex);
      }
    });

    // Listen for lightbox update events when products change
    document.addEventListener('stl:lightbox:update', handleLightboxUpdate);

    // Listen for escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isOpen) {
        closeLightbox();
      }
    });
  };

  // Initialize
  await init();

  // Cleanup
  return () => {
    if (slider) {
      slider.destroy(true, true);
    }

    node.removeEventListener('click', handleZoomButtonClick);
    document.removeEventListener('stl:lightbox:update', handleLightboxUpdate);
  };
});
